import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Code2, Plus, Trash2, Edit2, Save, X, Tag, Info } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  total: number;
  is_discount?: boolean;  // Flag to identify discounts/coupons
}

export interface InvoiceData {
  line_items: LineItem[];
  total_amount: number;
  subtotal?: number;
  tax_amount?: number;
  payment_method?: string;
  balance?: number;
  invoice_date?: string;
  invoice_number?: string;
}

interface InvoiceFieldEditorProps {
  invoiceData: InvoiceData;
  onSave: (correctedData: InvoiceData) => void;
  onCancel?: () => void;
  isReadOnly?: boolean;
  previewUrl?: string | null;
  fileName?: string;
  fileType?: 'image' | 'pdf' | 'csv' | 'excel' | 'unknown';
  fileUrl?: string; // For CSV/Excel files, we need the file URL to parse
  taggunRawData?: any; // Taggun OCR raw response JSON
}

const PAYMENT_METHODS = [
  'Credit Card',
  'Debit Card',
  'Cash',
  'Check',
  'ACH',
  'Wire Transfer',
  'Venmo',
  'PayPal',
  'Zelle',
  'Other',
];

export const InvoiceFieldEditor = ({
  invoiceData,
  onSave,
  onCancel,
  isReadOnly = false,
  previewUrl,
  fileName,
  fileType = 'unknown',
  fileUrl,
  taggunRawData,
}: InvoiceFieldEditorProps) => {
  const [csvPreviewData, setCsvPreviewData] = useState<{ headers: string[]; rows: any[][] } | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [showTaggunModal, setShowTaggunModal] = useState(false);

  // Load CSV/Excel preview data
  useEffect(() => {
    if ((fileType === 'csv' || fileType === 'excel') && fileUrl && !previewUrl) {
      loadCsvPreview();
    }
  }, [fileType, fileUrl, previewUrl]);

  const loadCsvPreview = async () => {
    if (!fileUrl) return;
    
    setCsvLoading(true);
    try {
      // Get signed URL
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('company-documents')
        .createSignedUrl(fileUrl, 60);

      if (signedUrlError || !signedUrlData?.signedUrl) {
        throw new Error('Failed to get file URL');
      }

      // Fetch the file
      const response = await fetch(signedUrlData.signedUrl);
      const blob = await response.blob();
      const file = new File([blob], fileName || 'file');

      if (fileType === 'csv') {
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          complete: (results) => {
            const rows = results.data as any[][];
            if (rows.length > 0) {
              const maxCols = Math.max(...rows.map(row => row.length));
              const normalizedRows = rows.map(row => {
                const normalized = [...row];
                while (normalized.length < maxCols) {
                  normalized.push('');
                }
                return normalized;
              });
              
              const firstRow = normalizedRows[0];
              const looksLikeHeader = firstRow.some(cell => 
                cell && typeof cell === 'string' && isNaN(Number(cell)) && cell.length > 0
              );
              
              if (looksLikeHeader) {
                setCsvPreviewData({
                  headers: firstRow.map((h, i) => h?.toString() || `Column ${i + 1}`),
                  rows: normalizedRows.slice(1),
                });
              } else {
                setCsvPreviewData({
                  headers: Array.from({ length: maxCols }, (_, i) => `Column ${i + 1}`),
                  rows: normalizedRows,
                });
              }
            }
            setCsvLoading(false);
          },
          error: () => setCsvLoading(false),
        });
      } else if (fileType === 'excel') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];
            
            if (jsonData.length > 0) {
              const maxCols = Math.max(...jsonData.map(row => row.length));
              const normalizedRows = jsonData.map(row => {
                const normalized = [...row];
                while (normalized.length < maxCols) {
                  normalized.push('');
                }
                return normalized.map(cell => cell?.toString() || '');
              });
              
              const firstRow = normalizedRows[0];
              const looksLikeHeader = firstRow.some(cell => 
                cell && typeof cell === 'string' && isNaN(Number(cell)) && cell.length > 0
              );
              
              if (looksLikeHeader) {
                setCsvPreviewData({
                  headers: firstRow.map((h, i) => h || `Column ${i + 1}`),
                  rows: normalizedRows.slice(1).filter(row => row.some(cell => cell && cell.trim() !== '')),
                });
              } else {
                setCsvPreviewData({
                  headers: Array.from({ length: maxCols }, (_, i) => `Column ${i + 1}`),
                  rows: normalizedRows.filter(row => row.some(cell => cell && cell.trim() !== '')),
                });
              }
            }
            setCsvLoading(false);
          } catch (error) {
            console.error('Error parsing Excel:', error);
            setCsvLoading(false);
          }
        };
        reader.onerror = () => setCsvLoading(false);
        reader.readAsBinaryString(file);
      }
    } catch (error) {
      console.error('Error loading CSV/Excel preview:', error);
      setCsvLoading(false);
    }
  };

  const getColumnLetter = (index: number): string => {
    let result = '';
    let num = index;
    while (num >= 0) {
      result = String.fromCharCode((num % 26) + 65) + result;
      num = Math.floor(num / 26) - 1;
    }
    return result;
  };
  const [data, setData] = useState<InvoiceData>(invoiceData);

  // Update data when invoiceData changes (e.g., when loading corrected data)
  useEffect(() => {
    setData(invoiceData);
  }, [invoiceData]);
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(null);
  const [newLineItem, setNewLineItem] = useState<Partial<LineItem>>({
    description: '',
    quantity: 1,
    unit: '',
    unit_price: 0,
    total: 0,
  });

  const handleLineItemChange = (id: string, field: keyof LineItem, value: any) => {
    setData((prev) => ({
      ...prev,
      line_items: prev.line_items.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          // Auto-calculate total if quantity or unit_price changes
          if (field === 'quantity' || field === 'unit_price') {
            updated.total = updated.quantity * updated.unit_price;
          }
          // Auto-detect discount if total becomes negative or description contains discount keywords
          if (field === 'description' || field === 'total' || field === 'unit_price') {
            const descLower = updated.description.toLowerCase();
            const discountKeywords = ['coupon', 'discount', 'promo', 'promotion', 'rebate', 'credit', 'off', 'save'];
            updated.is_discount = updated.total < 0 || updated.unit_price < 0 || 
              discountKeywords.some(keyword => descLower.includes(keyword));
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  const handleAddLineItem = () => {
    const id = `new-${Date.now()}`;
    const total = (newLineItem.quantity || 1) * (newLineItem.unit_price || 0);
    const descLower = (newLineItem.description || '').toLowerCase();
    const discountKeywords = ['coupon', 'discount', 'promo', 'promotion', 'rebate', 'credit', 'off', 'save'];
    const isDiscount = total < 0 || (newLineItem.unit_price || 0) < 0 || 
      discountKeywords.some(keyword => descLower.includes(keyword));
    
    const item: LineItem = {
      id,
      description: newLineItem.description || '',
      quantity: newLineItem.quantity || 1,
      unit: newLineItem.unit || '',
      unit_price: newLineItem.unit_price || 0,
      total,
      is_discount: isDiscount,
    };
    setData((prev) => ({
      ...prev,
      line_items: [...prev.line_items, item],
    }));
    setNewLineItem({
      description: '',
      quantity: 1,
      unit: '',
      unit_price: 0,
      total: 0,
    });
  };

  const handleDeleteLineItem = (id: string) => {
    setData((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((item) => item.id !== id),
    }));
  };

  const handleSave = () => {
    // Calculate totals if not provided
    const subtotal = data.subtotal ?? data.line_items.reduce((sum, item) => sum + item.total, 0);
    const tax = data.tax_amount ?? 0;
    const total = data.total_amount ?? subtotal + tax;
    const balance = data.balance ?? 0;

    onSave({
      ...data,
      subtotal,
      tax_amount: tax,
      total_amount: total,
      balance,
    });
  };

  const calculatedSubtotal = data.line_items.reduce((sum, item) => sum + item.total, 0);
  const calculatedTotal = (data.subtotal ?? calculatedSubtotal) + (data.tax_amount ?? 0);

  // Preview component
  const renderPreview = () => {
    if (!previewUrl) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <p>Preview not available</p>
        </div>
      );
    }

    if (fileType === 'image') {
      return (
        <div className="flex items-center justify-center h-full overflow-auto bg-muted/30">
          <img
            src={previewUrl}
            alt={fileName || 'Invoice preview'}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    if (fileType === 'pdf') {
      return (
        <div className="w-full h-full border rounded-lg overflow-hidden">
          <iframe
            src={previewUrl}
            className="w-full h-full"
            title={fileName || 'Invoice preview'}
          />
        </div>
      );
    }

    // CSV/Excel preview
    if (fileType === 'csv' || fileType === 'excel') {
      if (csvLoading) {
        return (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        );
      }

      if (csvPreviewData) {
        return (
          <div className="h-full overflow-auto border rounded-lg bg-white">
            <table className="border-collapse w-full" style={{ minWidth: 'max-content' }}>
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="bg-[#f3f3f3] border border-[#d4d4d4] px-1 py-1.5 text-[10px] font-medium text-gray-500 text-center w-8 min-w-[32px] sticky left-0 z-20"></th>
                  {csvPreviewData.headers.map((header, idx) => (
                    <th 
                      key={idx} 
                      className="bg-[#f3f3f3] border border-[#d4d4d4] px-3 py-1.5 text-xs font-medium text-gray-700 text-left min-w-[100px] max-w-[300px] whitespace-nowrap"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvPreviewData.rows.slice(0, 100).map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-blue-50/50">
                    <td className="bg-[#f3f3f3] border border-[#d4d4d4] px-1 py-1 text-[10px] font-medium text-gray-500 text-center w-8 min-w-[32px] sticky left-0">
                      {rowIdx + 1}
                    </td>
                    {csvPreviewData.headers.map((_, colIdx) => (
                      <td 
                        key={colIdx} 
                        className="bg-white border border-[#e5e5e5] px-3 py-1 text-sm text-gray-800 whitespace-nowrap"
                      >
                        {row[colIdx]?.toString() || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {csvPreviewData.rows.length > 100 && (
              <div className="p-2 text-center text-xs text-gray-600 bg-[#f3f3f3] border-t border-[#d4d4d4]">
                Showing first 100 of {csvPreviewData.rows.length} rows
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <p>Unable to load preview</p>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>Preview not available for this file type</p>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
      {/* Left: Editable Invoice Data */}
      <div className="space-y-6 overflow-y-auto pr-2">
      {/* Invoice Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Invoice Number</Label>
              <Input
                value={data.invoice_number || ''}
                onChange={(e) => setData({ ...data, invoice_number: e.target.value })}
                disabled={isReadOnly}
                placeholder="Invoice #"
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice Date</Label>
              <Input
                type="date"
                value={data.invoice_date || ''}
                onChange={(e) => setData({ ...data, invoice_date: e.target.value })}
                disabled={isReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={data.payment_method || ''}
                onValueChange={(value) => setData({ ...data, payment_method: value })}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Line Items</CardTitle>
            {!isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddLineItem}
                disabled={!newLineItem.description}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p className="text-sm">
                <strong>Quantity and Unit columns:</strong> These values are used to calculate cost-per-unit and link to inventory items during ingredient mapping in Step 5. 
                Ensure these are accurate for proper cost tracking.
              </p>
            </AlertDescription>
          </Alert>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">Description</TableHead>
                  <TableHead className="text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">Qty</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Quantity values are used to calculate cost-per-unit and link to inventory items during ingredient mapping in Step 5.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">Unit</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Unit values are used to calculate cost-per-unit and link to inventory items during ingredient mapping in Step 5.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-center w-[70px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center justify-center gap-1">
                            <Tag className="h-3 w-3" />
                            Coupon
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Mark as coupon/discount to exclude from price trend calculations</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  {!isReadOnly && <TableHead className="w-[60px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.line_items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {editingLineItemId === item.id ? (
                        <Input
                          value={item.description}
                          onChange={(e) =>
                            handleLineItemChange(item.id, 'description', e.target.value)
                          }
                          onBlur={() => setEditingLineItemId(null)}
                          autoFocus
                        />
                      ) : (
                        <div
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={() => !isReadOnly && setEditingLineItemId(item.id)}
                        >
                          <span>{item.description || 'Click to edit'}</span>
                          {!isReadOnly && <Edit2 className="h-3 w-3 text-muted-foreground" />}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isReadOnly ? (
                        item.quantity
                      ) : (
                        <Input
                          type="number"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            const parsed = value === '' ? 1 : parseFloat(value);
                            handleLineItemChange(item.id, 'quantity', isNaN(parsed) ? 1 : Math.max(1, parsed));
                          }}
                          className="w-20 text-right"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isReadOnly ? (
                        item.unit || '-'
                      ) : (
                        <Input
                          value={item.unit || ''}
                          onChange={(e) => handleLineItemChange(item.id, 'unit', e.target.value)}
                          placeholder="Unit"
                          className="w-20 text-right"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isReadOnly ? (
                        <span className={item.unit_price < 0 ? 'text-red-600' : ''}>
                          {item.unit_price < 0 
                            ? `-$${Math.abs(item.unit_price).toFixed(2)}` 
                            : `$${item.unit_price.toFixed(2)}`}
                        </span>
                      ) : (
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => {
                            const value = e.target.value;
                            const parsed = value === '' || value === '-' ? 0 : parseFloat(value);
                            handleLineItemChange(item.id, 'unit_price', isNaN(parsed) ? 0 : parsed);
                          }}
                          className={`w-24 text-right ${item.unit_price < 0 ? 'text-red-600' : ''}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${item.total < 0 ? 'text-red-600' : ''}`}>
                      {item.total < 0 
                        ? `-$${Math.abs(item.total).toFixed(2)}` 
                        : `$${item.total.toFixed(2)}`}
                    </TableCell>
                    <TableCell className="text-center">
                      {isReadOnly ? (
                        item.is_discount && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                            Coupon
                          </Badge>
                        )
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex justify-center">
                                <Checkbox
                                  checked={item.is_discount || false}
                                  onCheckedChange={(checked) => 
                                    handleLineItemChange(item.id, 'is_discount', Boolean(checked))
                                  }
                                  className={item.is_discount ? 'border-orange-500 data-[state=checked]:bg-orange-500' : ''}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{item.is_discount ? 'This item is marked as a coupon/discount' : 'Click to mark as coupon/discount'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteLineItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!isReadOnly && (
                  <TableRow>
                    <TableCell colSpan={6} className="border-t">
                      <div className="flex gap-2 py-2">
                        <Input
                          placeholder="Description"
                          value={newLineItem.description || ''}
                          onChange={(e) =>
                            setNewLineItem({ ...newLineItem, description: e.target.value })
                          }
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Qty"
                          value={newLineItem.quantity || ''}
                          onChange={(e) =>
                            setNewLineItem({
                              ...newLineItem,
                              quantity: parseFloat(e.target.value) || 1,
                            })
                          }
                          className="w-20"
                        />
                        <Input
                          placeholder="Unit"
                          value={newLineItem.unit || ''}
                          onChange={(e) => setNewLineItem({ ...newLineItem, unit: e.target.value })}
                          className="w-20"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Price"
                          value={newLineItem.unit_price || ''}
                          onChange={(e) =>
                            setNewLineItem({
                              ...newLineItem,
                              unit_price: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="w-24"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddLineItem}
                          disabled={!newLineItem.description}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      <Card>
        <CardHeader>
          <CardTitle>Totals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-end">
            <div className="w-full md:w-[320px] space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                {isReadOnly ? (
                  <span className="font-medium">${(data.subtotal ?? calculatedSubtotal).toFixed(2)}</span>
                ) : (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={data.subtotal ?? calculatedSubtotal}
                    onChange={(e) =>
                      setData({ ...data, subtotal: parseFloat(e.target.value) || calculatedSubtotal })
                    }
                    className="w-32 text-right"
                  />
                )}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax:</span>
                {isReadOnly ? (
                  <span className="font-medium">${(data.tax_amount ?? 0).toFixed(2)}</span>
                ) : (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={data.tax_amount ?? 0}
                    onChange={(e) =>
                      setData({ ...data, tax_amount: parseFloat(e.target.value) || 0 })
                    }
                    className="w-32 text-right"
                  />
                )}
              </div>
              <div className="flex justify-between text-sm pt-2 border-t font-semibold">
                <span>Total:</span>
                {isReadOnly ? (
                  <span className="text-primary">${(data.total_amount ?? calculatedTotal).toFixed(2)}</span>
                ) : (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={data.total_amount ?? calculatedTotal}
                    onChange={(e) =>
                      setData({ ...data, total_amount: parseFloat(e.target.value) || calculatedTotal })
                    }
                    className="w-32 text-right font-semibold"
                  />
                )}
              </div>
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="text-muted-foreground">Payment Method:</span>
                {isReadOnly ? (
                  <span className="font-medium">{data.payment_method || 'N/A'}</span>
                ) : (
                  <Input
                    type="text"
                    value={data.payment_method || ''}
                    onChange={(e) =>
                      setData({ ...data, payment_method: e.target.value })
                    }
                    placeholder="e.g., DEBIT 5152, CASH, CHECK"
                    className="w-32 text-right"
                  />
                )}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Balance:</span>
                {isReadOnly ? (
                  <span className="font-medium">${(data.balance ?? 0).toFixed(2)}</span>
                ) : (
                  <Input
                    type="number"
                    step="0.01"
                    value={data.balance ?? 0}
                    onChange={(e) =>
                      setData({ ...data, balance: parseFloat(e.target.value) || 0 })
                    }
                    className="w-32 text-right"
                  />
                )}
              </div>
              {!isReadOnly && (
                <div className="pt-2">
                  <Badge variant="outline" className="text-xs">
                    Calculated: ${calculatedTotal.toFixed(2)}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

        {/* Action Buttons */}
        {!isReadOnly && (
          <div className="flex justify-end gap-2 pt-4 border-t">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Corrections
            </Button>
          </div>
        )}
      </div>

      {/* Right: Invoice Preview */}
      <div className="lg:border-l lg:pl-6 border-t lg:border-t-0 pt-6 lg:pt-0">
        <div className="lg:sticky lg:top-0">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <Label className="text-sm font-semibold">Invoice Preview</Label>
              {fileName && (
                <p className="text-xs text-muted-foreground mt-1">{fileName}</p>
              )}
            </div>
            {taggunRawData && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTaggunModal(true)}
                className="h-8 w-8 p-0"
                title="View Taggun OCR Response (Dev)"
              >
                <Code2 className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </Button>
            )}
          </div>
          <div className="h-[400px] lg:h-[calc(100vh-200px)] border rounded-lg overflow-hidden bg-background">
            {renderPreview()}
          </div>
        </div>
      </div>

      {/* Taggun Response Modal */}
      <Dialog open={showTaggunModal} onOpenChange={setShowTaggunModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] z-[100]" style={{ zIndex: 100 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-5 w-5" />
              Taggun OCR Response (Raw JSON)
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[80vh] pr-4">
            <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg overflow-auto text-xs font-mono">
              {JSON.stringify(taggunRawData, null, 2)}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

